import { useState } from "react";

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function getAuthInputStyle() {
  return {
    width: "100%",
    padding: "12px 14px",
    borderRadius: "14px",
    border: "2px solid #bfdbfe",
    background: "#f5faff",
    color: "#1e3a5f",
    fontSize: "15px",
    boxSizing: "border-box",
    outline: "none",
  };
}

// ── Schedule Edit Modal ───────────────────────────────────────────────────────
export function SideQuestScheduleModal({ quest, existing, todayKey, onSave, onClose }) {
  const [localType, setLocalType] = useState(existing?.type || "recurring");
  const [localDays, setLocalDays] = useState(existing?.days || []);
  const [localDate, setLocalDate] = useState(existing?.oneTimeDate || todayKey);

  function toggleDay(d) {
    setLocalDays((prev) =>
      prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]
    );
  }

  function handleSave() {
    onSave({
      type: localType,
      days: localType === "recurring" ? localDays : [],
      oneTimeDate: localType === "once" ? localDate : null,
      movedDates: existing?.movedDates || {},
    });
  }

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(30,58,138,0.35)",
        zIndex: 1000, display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#f5faff", borderRadius: "24px 24px 0 0", padding: "24px",
          width: "100%", maxWidth: "480px", boxSizing: "border-box",
          boxShadow: "0 -8px 32px rgba(30,58,138,0.18)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "18px", marginBottom: "4px" }}>
          {quest.emoji} Schedule "{quest.title}"
        </div>
        <p style={{ color: "#6b8cb0", fontSize: "13px", marginTop: 0, marginBottom: "16px" }}>
          Choose when this quest should appear on your dashboard automatically.
        </p>

        {/* Type toggle */}
        <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
          {[{ key: "recurring", label: "🔁 Recurring" }, { key: "once", label: "📅 One Time" }].map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setLocalType(t.key)}
              style={{
                flex: 1, padding: "10px", borderRadius: "14px", fontWeight: "bold",
                fontSize: "14px", cursor: "pointer",
                border: localType === t.key ? "2px solid #2563eb" : "2px solid #bfdbfe",
                background: localType === t.key ? "#e0f2fe" : "#f8fbff", color: "#1e3a5f",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {localType === "recurring" && (
          <div>
            <div style={{ color: "#4b6b8a", fontSize: "12px", fontWeight: "bold", marginBottom: "8px", textTransform: "uppercase" }}>
              Repeat on these days
            </div>
            <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
              {DAY_NAMES.map((name, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => toggleDay(idx)}
                  style={{
                    padding: "8px 12px", borderRadius: "12px", fontWeight: "bold",
                    fontSize: "13px", cursor: "pointer",
                    border: localDays.includes(idx) ? "2px solid #2563eb" : "2px solid #bfdbfe",
                    background: localDays.includes(idx) ? "#e0f2fe" : "#f8fbff", color: "#1e3a5f",
                  }}
                >
                  {name}
                </button>
              ))}
            </div>
          </div>
        )}

        {localType === "once" && (
          <div>
            <div style={{ color: "#4b6b8a", fontSize: "12px", fontWeight: "bold", marginBottom: "8px", textTransform: "uppercase" }}>
              Date
            </div>
            <input
              type="date"
              value={localDate}
              onChange={(e) => setLocalDate(e.target.value)}
              style={getAuthInputStyle()}
            />
          </div>
        )}

        <div style={{ display: "flex", gap: "8px", marginTop: "20px" }}>
          <button
            type="button"
            onClick={handleSave}
            style={{
              flex: 1, padding: "12px", borderRadius: "14px", border: "none",
              background: "#2563eb", color: "#fff", fontWeight: "bold", fontSize: "14px", cursor: "pointer",
            }}
          >
            Save Schedule
          </button>
          {existing && (
            <button
              type="button"
              onClick={() => onSave(null)}
              style={{
                padding: "12px 16px", borderRadius: "14px", border: "2px solid #fca5a5",
                background: "#fff1f2", color: "#dc2626", fontWeight: "bold", fontSize: "14px", cursor: "pointer",
              }}
            >
              Remove
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Move Day Modal ────────────────────────────────────────────────────────────
export function SideQuestMoveDayModal({ quest, fromDateKey, onMove, onClose }) {
  // Build next 7 days (excluding fromDateKey)
  const options = [];
  for (let i = -3; i <= 6; i++) {
    const d = new Date(`${fromDateKey}T12:00:00`);
    d.setDate(d.getDate() + i);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    if (key !== fromDateKey) {
      options.push({
        key,
        label: d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }),
      });
    }
  }

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(30,58,138,0.35)",
        zIndex: 1000, display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#f5faff", borderRadius: "24px 24px 0 0", padding: "24px",
          width: "100%", maxWidth: "480px", boxSizing: "border-box",
          boxShadow: "0 -8px 32px rgba(30,58,138,0.18)",
          maxHeight: "70vh", overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "18px", marginBottom: "4px" }}>
          {quest.emoji} Move "{quest.title}"
        </div>
        <p style={{ color: "#6b8cb0", fontSize: "13px", marginTop: 0, marginBottom: "16px" }}>
          Move this quest from <strong>{new Date(`${fromDateKey}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}</strong> to another day this week.
        </p>

        <div style={{ display: "grid", gap: "8px" }}>
          {options.map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => onMove(opt.key)}
              style={{
                width: "100%", padding: "12px 16px", borderRadius: "14px",
                border: "2px solid #bfdbfe", background: "#f8fbff",
                color: "#1e3a5f", fontWeight: "bold", fontSize: "14px",
                cursor: "pointer", textAlign: "left",
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={onClose}
          style={{
            width: "100%", marginTop: "14px", padding: "12px", borderRadius: "14px",
            border: "2px solid #bfdbfe", background: "#f5faff",
            color: "#4b6b8a", fontWeight: "bold", fontSize: "14px", cursor: "pointer",
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
